import { AppBar, Avatar, Badge, Box, IconButton, Toolbar, Tooltip, Typography } from "@mui/material";
import { useContext } from "react";
import { FaBars, FaSignOutAlt } from "react-icons/fa";
import { FaCartPlus, FaCircleUser, FaEnvelope } from "react-icons/fa6";
import { AiFillGold } from "react-icons/ai";
import { Link } from "react-router-dom";
import { useAuth } from "../../providers/AuthProvider";
import { moneyFormat } from "../../utils/functions";
import { GoldPriceContext } from "../../providers/GoldPriceProvider";
import { usePermissions } from "../../providers/PermissionsProvider";
import { usePendingQuotes } from "../../providers/PendingQuotesProvider";
import { DRAWER_PERMANENT_BP, DRAWER_RAIL, DRAWER_WIDTH } from "../AdminDrawer/widths";
import NotificationsBell from "./NotificationsBell";

const ink = "#0B0A08";
const gold = "#C8A459";

export default function Header({ handleVisibilityChange, isExpanded = false }) {
    const { verifyPermission } = usePermissions();
    const { totalQuotes } = usePendingQuotes();
    const { signOut, user } = useAuth();
    const { id, has_image } = user;
    const { currentGoldPrice } = useContext(GoldPriceContext);
    const drawerWidth = isExpanded ? DRAWER_WIDTH : DRAWER_RAIL;
    const breakCacheVariable = btoa(new Date());

    const profileImage = has_image ? (
        <Avatar
            src={`https://inovartgold.com.br/assets/user-image/${id}.jpg?${breakCacheVariable}`}
            sx={{ width: 32, height: 32 }}
        />
    ) : (
        <FaCircleUser size={22} />
    );

    const actions = [
        {
            key: "menu",
            title: "Menu",
            onClick: handleVisibilityChange,
            icon: <FaBars />,
            mobileOnly: true,
        },
        verifyPermission("orders", "2") && {
            key: "new-order",
            title: "Cadastrar pedido",
            component: Link,
            to: "/pedido-novo",
            icon: <FaCartPlus size={20} />,
            accent: true,
        },
        verifyPermission("price", "1") && {
            key: "gold",
            title: "Cotação",
            component: Link,
            to: "/cotacao",
            icon: <AiFillGold size={20} />,
            label: `R$ ${moneyFormat(currentGoldPrice)}`,
        },
        verifyPermission("quotation", "1") && {
            key: "quotes",
            title: "Orçamentos",
            component: Link,
            to: "/orcamentos",
            icon: <FaEnvelope size={18} />,
            badge: totalQuotes,
        },
        {
            key: "notifications",
            title: "Notificações",
            custom: true,
            render: () => <NotificationsBell />,
        },
        {
            key: "profile",
            title: "Meu perfil",
            component: Link,
            to: "/perfil",
            icon: profileImage,
        },
        {
            key: "logout",
            title: "Sair",
            onClick: signOut,
            icon: <FaSignOutAlt size={18} />,
        },
    ].filter(Boolean);

    const menuAction = actions.find((action) => action.key === "menu");
    const toolbarActions = actions.filter((action) => action.key !== "menu");

    const renderAction = (action) => (
        action.custom ? (
            <Box key={action.key} component="span">
                {action.render()}
            </Box>
        ) : (
            <Tooltip key={action.key} title={action.title}>
                <IconButton
                    component={action.component || "button"}
                    to={action.to}
                    onClick={action.onClick}
                    aria-label={action.title}
                    sx={{
                        color: action.accent ? gold : ink,
                        display: action.mobileOnly
                            ? { xs: "inline-flex", [DRAWER_PERMANENT_BP]: "none" }
                            : "inline-flex",
                        minWidth: 44,
                        minHeight: 44,
                        borderRadius: 1,
                        px: action.label ? 1.25 : 1,
                        "&:hover": {
                            bgcolor: "rgba(200,164,89,0.12)",
                            color: gold,
                        },
                    }}
                >
                    {action.badge ? (
                        <Badge
                            badgeContent={action.badge}
                            sx={{
                                "& .MuiBadge-badge": {
                                    bgcolor: gold,
                                    color: ink,
                                    fontWeight: 700,
                                    fontSize: 10,
                                },
                            }}
                        >
                            {action.icon}
                        </Badge>
                    ) : action.label ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {action.icon}
                            <Typography sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: gold,
                                letterSpacing: "0.02em",
                                display: { xs: "none", xl: "inline" },
                            }}>
                                {action.label}
                            </Typography>
                        </Box>
                    ) : (
                        action.icon
                    )}
                </IconButton>
            </Tooltip>
        )
    );

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                bgcolor: "#FFFFFF",
                color: ink,
                boxShadow: "none",
                borderBottom: "1px solid #E5E2DA",
                left: { xs: 0, [DRAWER_PERMANENT_BP]: `${drawerWidth}px` },
                width: { xs: "100%", [DRAWER_PERMANENT_BP]: `calc(100% - ${drawerWidth}px)` },
                transition: "left .25s ease, width .25s ease",
            }}
        >
            <Toolbar sx={{
                minHeight: { xs: 64, md: 64 },
                px: { xs: 1, sm: 1.5, md: 2.5 },
                justifyContent: "space-between",
                gap: 0.25,
                flexWrap: "nowrap",
                py: 0.5,
            }}>
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 44 }}>
                    {menuAction ? renderAction(menuAction) : null}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: "auto" }}>
                    {toolbarActions.map(renderAction)}
                </Box>
            </Toolbar>
        </AppBar>
    );
}
